"use client";

import { useState } from "react";

// ─── Exchange rates (AED base) ────────────────────────────────────────────────
const RATES = { AED: 1, USD: 3.67, EUR: 4.0 };
const SYMBOLS = { AED: "AED", USD: "USD", EUR: "EUR" };
const FLAGS = { AED: "🇦🇪", USD: "🇺🇸", EUR: "🇪🇺" };

function convertPrice(priceStr, currency) {
  const val = parseFloat(String(priceStr).replace(/,/g, ""));
  if (isNaN(val)) return priceStr;
  const rate = RATES[currency];
  const converted = val / rate;
  let rounded;
  if (currency === "AED") {
    rounded = val;
  } else if (converted < 100) {
    rounded = Math.round(converted / 5) * 5;
  } else if (converted < 1000) {
    rounded = Math.round(converted / 10) * 10;
  } else {
    rounded = Math.round(converted / 50) * 50;
  }
  return rounded.toLocaleString();
}

// ─── Full Pricing Catalog ─────────────────────────────────────────────────────
const pricingCatalog = {
  "web-app-development": {
    subtitle: "Application Packages",
    title: "20 Web & App Pricing Packages",
    description: "Select your application type to view tailored pricing, features, and delivery timelines for your digital solution.",
    type: "tabbed",
    categories: [
      {
        id: "cat-corporate", name: "Corporate & Static",
        items: [
          { name: "Single Page Landing", timeline: "5-7 Days", price: "750", features: ["Custom Visual Section Design", "Inbound Lead Form Integration", "Speed & Mobile Responsive", "Google Analytics Tracking"] },
          { name: "Corporate Website", timeline: "10-14 Days", price: "3,500", features: ["Up to 7 Content Pages", "About, Services & Contact Page", "Admin CMS Panel (Edit Content)", "Basic SEO Optimization"], active: true },
          { name: "Blog / Content Platform", timeline: "8-12 Days", price: "3,000", features: ["Layout for Article Publishing", "Newsletter Signup & Mailer sync", "Social Media Auto-share module", "AdSense & Banner Ad ready"] },
          { name: "Creative Portfolio", timeline: "7-10 Days", price: "2,800", features: ["Dynamic Grid Filter Gallery", "High-quality Image/Video viewer", "Resume / Client Reviews section", "Smooth Hover micro-animations"] },
          { name: "Restaurant Order App", timeline: "12-16 Days", price: "4,500", features: ["Food Menu item categorization", "Cart & Checkout delivery fields", "WhatsApp / Print Receipt order API", "Google Maps delivery radius"] },
        ]
      },
      {
        id: "cat-ecommerce", name: "E-Commerce & Retail",
        items: [
          { name: "WooCommerce Shop", timeline: "14-20 Days", price: "5,000", features: ["WordPress + WooCommerce configuration", "Product Filters & Attribute management", "Integrated Payment Gateway (Stripe/Tabby)", "Order invoice PDF automation"] },
          { name: "Shopify Custom Store", timeline: "12-18 Days", price: "6,500", features: ["Shopify Custom Liquid theme tuning", "Product upload & Variants setup", "Middle East & African RegionLocal Delivery/VAT configurations", "Apps & Cart Recovery integrations"], active: true },
          { name: "Custom React Shop", timeline: "25-35 Days", price: "12,000", features: ["Next.js Frontend + Node/Express backend", "Ultra-fast response with MongoDB database", "Custom Admin Dashboard for Inventory", "API integrations with ERP & CRM"] },
          { name: "Appointment & Booking", timeline: "15-22 Days", price: "6,000", features: ["Dynamic Date & Slot selector calendar", "Automatic Email/SMS booking alerts", "Client portal to view & cancel reservations", "Payment deposits / Full fee integration"] },
          { name: "Event Ticketing App", timeline: "18-24 Days", price: "7,000", features: ["Custom Ticket QR Code generator", "Seat assignment & Pricing tiers", "Mobile Ticket entry scanner endpoint", "Payout & Sales analytics charts"] },
        ]
      },
      {
        id: "cat-portals", name: "Portals & Directories",
        items: [
          { name: "Real Estate Portal", timeline: "20-28 Days", price: "8,000", features: ["Map-based search & coordinates listings", "Agent Registration & Property upload", "Advanced Search (Beds, Price, Area filters)", "WhatsApp listing share & Callback button"] },
          { name: "Job & Recruitment Portal", timeline: "22-30 Days", price: "9,000", features: ["Candidate Resume upload & Profile builder", "Employer Job Post & Applicant tracking", "Automatic email notifications on matches", "Subscription packages for job ads"], active: true },
          { name: "Classified Ads Portal", timeline: "18-25 Days", price: "7,500", features: ["Listing categorizations (Items, Cars, Jobs)", "User profile phone number SMS verification", "Featured / Premium listing top ranking packages", "Chat module between buyer & seller"] },
          { name: "Healthcare Appointment", timeline: "16-22 Days", price: "6,500", features: ["Doctor profiles & specialization sorting", "Real-time consultation booking grid", "Patient login panel for prescriptions", "Telehealth / Video calling ready API"] },
          { name: "Automotive / Car Rental", timeline: "20-26 Days", price: "8,000", features: ["Vehicle listings sorting & filter attributes", "Calendar availability checks & deposit billing", "Driving License & ID document upload", "WhatsApp rental request routing API"] },
        ]
      },
      {
        id: "cat-enterprise", name: "Enterprise & SaaS",
        items: [
          { name: "SaaS Platform MVP", timeline: "30-45 Days", price: "15,000", features: ["Multi-tenant account subscriptions setup", "User authentication / Roles / Permissions", "Payment gateway subscription billing integration", "Developer API access endpoints ready"] },
          { name: "Enterprise ERP/CRM", timeline: "45-60 Days", price: "25,000", features: ["Fully tailored workflow automation suite", "Sales pipelines, invoice databases, HR systems", "Dynamic real-time chart modules & reports", "On-premise or Private cloud hosting setups"], active: true },
          { name: "e-Learning LMS", timeline: "20-30 Days", price: "8,500", features: ["Video course structures & quizzes modules", "Student, Teacher, and Admin login dashboards", "Auto-generated PDF course certificates", "Pay-per-course / Subscription models"] },
          { name: "Helpdesk Support", timeline: "18-25 Days", price: "8,000", features: ["Ticket submission & Auto-assignment", "SLA tracking alerts & Agent dashboards", "Knowledge Base article management", "Client portal to view ticket statuses"] },
          { name: "API Backend Stack", timeline: "12-20 Days", price: "5,500", features: ["REST / GraphQL API endpoints development", "Secure OAuth2/JWT token verification", "Payment/Shipping webhook handlers", "Complete OpenAPI/Swagger documentation"] },
        ]
      },
    ]
  },
  "ui-ux-design": {
    subtitle: "Design Packages",
    title: "20 UI/UX Design Pricing Packages",
    description: "Select your design deliverable category to view tailored pricing, features, and delivery timelines.",
    type: "tabbed",
    categories: [
      {
        id: "cat-ux-research", name: "UX Research & Strategy",
        items: [
          { name: "UX Usability Audit", timeline: "3-5 Days", price: "1,200", features: ["Comprehensive UI heuristic analysis", "Usability friction points checklist", "Conversion rate bottleneck mapping", "1 Hour video walk-through session"] },
          { name: "User Research & Personas", timeline: "5-7 Days", price: "1,500", features: ["Up to 5 targeted user interviews", "Detailed persona card profiles", "Customer journey maps (CJM)", "Pain-point & Expectation summary"], active: true },
          { name: "Information Architecture", timeline: "4-6 Days", price: "1,800", features: ["Card sorting UX exercises", "Hierarchical sitemap diagrams", "Screen sitemap & navigation flows", "Dynamic menu taxonomy layout"] },
          { name: "Lo-Fi Wireframing", timeline: "7-10 Days", price: "2,500", features: ["Up to 12 screen graybox layouts", "Visual content hierarchy grids", "Core user flows in Figma layout", "Clickable connection paths"] },
          { name: "Usability Testing", timeline: "5-8 Days", price: "2,200", features: ["5 Interactive testing sessions", "Audio/Video user screen records", "Actionable task checklist summary", "Recommended updates slide report"] },
        ]
      },
      {
        id: "cat-ui-visual", name: "Visual UI Design",
        items: [
          { name: "Landing Page UI", timeline: "4-6 Days", price: "1,500", features: ["1 Custom visually stunning landing layout", "Desktop & Responsive Mobile views", "Modern gradient & graphic variables", "Fully editable Figma source file"] },
          { name: "Corporate Web UI", timeline: "10-14 Days", price: "3,500", features: ["Up to 8 page visual templates", "Consistent grid systems & spacings", "Desktop + Tablet + Mobile frame specs", "Complete component style reference guide"], active: true },
          { name: "Mobile App UI", timeline: "12-18 Days", price: "5,000", features: ["Up to 15 Screens Visual Design", "Apple Human Interface & Material rules", "Interactive user flow click maps", "Assets configured for easy export"] },
          { name: "E-Commerce UI", timeline: "10-15 Days", price: "4,500", features: ["Catalog, details, checkout UX pathing", "Cart & slide-out menu animations", "Direct conversion rate design features", "Web & Mobile specific layouts"] },
          { name: "Dashboard UI", timeline: "14-20 Days", price: "6,000", features: ["Complex charts, statistics tables UI", "Custom sidebar & navbar system", "Filtering widgets & form actions", "Multi-role view configurations"] },
        ]
      },
      {
        id: "cat-design-systems", name: "Design Systems & Branding",
        items: [
          { name: "Brand Identity Guide", timeline: "5-8 Days", price: "2,000", features: ["Custom logo variations (vector)", "Corporate color theme palette rules", "Typography guidelines (fonts pairing)", "Brandbook PDF guide manual"] },
          { name: "Figma Design System", timeline: "8-12 Days", price: "3,800", features: ["Figma auto-layout button variations", "Dynamic typography & color tokens", "Custom form elements & dropdown variants", "Scalable layout grid references"], active: true },
          { name: "Icons & Graphics Kit", timeline: "5-7 Days", price: "1,800", features: ["50+ Custom branded line icons (SVG)", "Unified stroke weights & sizing grids", "5 Custom visual illustrations", "Configured for React/HTML export"] },
          { name: "Developer Hand-Off", timeline: "3-5 Days", price: "1,500", features: ["Figma exportable assets categorization", "Annotated layouts with spacing specs", "CSS variables generation script", "1 Round of direct dev consultation"] },
          { name: "Pitch Deck UI Layout", timeline: "4-6 Days", price: "1,500", features: ["15 Custom presentation slides", "Clean typography layouts for statistics", "High-impact visual slide hierarchy", "Figma & PDF editable exports"] },
        ]
      },
      {
        id: "cat-prototypes", name: "Prototypes & Motion",
        items: [
          { name: "Hi-Fi Prototype", timeline: "7-10 Days", price: "3,000", features: ["Smart Animate Figma transition maps", "Interactive side nav & header clicks", "Inputs simulation (forms typing demo)", "Link ready for client presentation"] },
          { name: "Micro-Interactions", timeline: "5-8 Days", price: "2,500", features: ["Hover & Click animations for buttons", "Loader indicators & progress bars", "Smooth overlay slides & popups triggers", "Keyframes documentation for developers"], active: true },
          { name: "Redesign Concept", timeline: "10-14 Days", price: "4,000", features: ["Before-and-after UI layout comparisons", "Usability problem solution designs", "Modernized styling variables", "Retained brand asset integration"] },
          { name: "MVP App UI/UX", timeline: "8-12 Days", price: "3,500", features: ["Up to 10 Essential Flow Screens", "User signup, Dashboard, Actions views", "UI component assets guidelines", "Direct interactive mockup link"] },
          { name: "SaaS Platform Workspace", timeline: "15-22 Days", price: "7,500", features: ["Multi-dashboard workspace UI", "Advanced filters sidebar grids UI", "Workspace settings & Billing screens", "High-fidelity prototype with interactions"] },
        ]
      },
    ]
  },
  "cloud-devops": {
    subtitle: "Pricing Plans", title: "Cloud & DevOps Packages", type: "simple",
    items: [
      { name: "DevOps Setup", price: "3,500", desc: "Core server deployment and containerization", features: ["Docker Containerization", "Single Server Infrastructure", "Basic CI/CD Setup (GitHub Actions)", "SSL & Domain Configuration", "30 Days Support"] },
      { name: "CI/CD Automation", price: "6,800", desc: "Automated software delivery pipelines", features: ["Docker & Kubernetes Setup", "Multi-stage CI/CD Pipelines", "Infrastructure as Code (Terraform)", "System Log Monitoring Setup", "90 Days Support"], active: true },
      { name: "Enterprise Cloud", price: "12,000", desc: "Highly available server cluster setup", features: ["Kubernetes Autoscaling Cluster", "Multi-region Redundancy", "WAF & Advanced DDoS Shield", "Disaster Recovery Blueprint", "1 Year SLA Management"] },
    ]
  },
  "vps-hosting": {
    subtitle: "Pricing Plans", title: "VPS & Hosting Packages", type: "simple",
    items: [
      { name: "Managed VPS Starter", price: "250", desc: "Fast, secure VPS server with managed control", features: ["2 vCPU / 4GB RAM / 80GB SSD", "Free SSL & Domain Migration", "Daily Backups & Uptime Alerts", "cPanel / Plesk Control Panel", "24/7 Monitoring Support"], duration: "/mo" },
      { name: "Business Dedicated", price: "750", desc: "Dedicated resources for high-traffic sites", features: ["4 vCPU / 8GB RAM / 160GB SSD", "Isolated Dedicated Resources", "DDoS Protection Suite", "Weekly Security Audits", "Priority Support SLA"], duration: "/mo", active: true },
      { name: "Enterprise Cluster", price: "1,800", desc: "Load-balanced high-performance hosting", features: ["High Availability Clusters", "Redis & Memcached Cache Setup", "Automated Daily Offsite Backups", "Dedicated Server Ops Team", "15-min Response Time SLA"], duration: "/mo" },
    ]
  },
  "aws-cloud-platforms": {
    subtitle: "Pricing Plans", title: "AWS & Cloud Platform Packages", type: "simple",
    items: [
      { name: "AWS Basic Setup", price: "3,000", desc: "Core AWS cloud architecture setup", features: ["AWS EC2 Server Configuration", "AWS S3 Asset Storage Setup", "VPC & Security Group Tuning", "Route 53 Domain Routing", "30 Days Infrastructure Support"] },
      { name: "AWS RDS & Serverless", price: "6,500", desc: "Modern serverless application setup", features: ["AWS Lambda & API Gateway", "AWS RDS Database Integration", "AWS CloudWatch Monitoring Alerts", "Automated Backup Procedures", "90 Days Infrastructure Support"], active: true },
      { name: "AWS High Availability", price: "12,500", desc: "Fail-safe cluster configuration on AWS", features: ["Auto-scaling & Load Balancer", "Multi-AZ Database Clustering", "AWS CloudFront CDN Setup", "AWS WAF Firewall Security", "1 Year Enterprise Operations"] },
    ]
  },
  "server-management": {
    subtitle: "Pricing Plans", title: "Server Management Packages", type: "simple",
    items: [
      { name: "Single Server Admin", price: "450", desc: "Comprehensive monitoring and maintenance", features: ["OS Updates & Security Patches", "24/7 Service Monitoring Alerts", "Apache / Nginx Optimizations", "Daily Backup Verifications", "Email & Ticket Support"], duration: "/mo" },
      { name: "Standard Multi-Server", price: "1,200", desc: "Complete support for multiple servers", features: ["Up to 5 Servers Monitored", "Advanced Security Hardening", "Database Tuning & Optimizations", "DDoS Assessment & Protection", "1-hour Response Time SLA"], duration: "/mo", active: true },
      { name: "High-Performance", price: "2,500", desc: "Dedicated Server Administrator support", features: ["Unlimited Server Deployments", "Custom High-Load Tuning", "Log Consolidation & Auditing", "Weekly Vulnerability Scans", "24/7 Hotline Support SLA"], duration: "/mo" },
    ]
  },
  "ai-data": {
    subtitle: "Pricing Plans", title: "AI & Data Packages", type: "simple",
    items: [
      { name: "Business Dashboards", price: "4,500", desc: "Data visualization and metrics analytics", features: ["Data Integration (SQL, API, CSV)", "Interactive PowerBI / Custom UI", "Real-time Chart Updates", "User Role Permissions Setup", "30 Days Training & Support"] },
      { name: "Custom AI Chatbot", price: "9,000", desc: "Intelligent customer interaction bots", features: ["LLM Integration (OpenAI, Anthropic)", "Company Knowledge Base Setup", "Multi-channel (Web, WhatsApp)", "CRM Lead Auto-capture", "90 Days Fine-tuning Support"], active: true },
      { name: "Predictive ML & RPA", price: "18,000", desc: "End-to-end business flow automation", features: ["Custom Machine Learning Model", "Automated Report Generation", "RPA Legacy System Integration", "Predictive Trend Analysis", "1 Year Technical Maintenance"] },
    ]
  },
  "cybersecurity": {
    subtitle: "Pricing Plans", title: "Cybersecurity Packages", type: "simple",
    items: [
      { name: "Security Audit", price: "2,000", desc: "Comprehensive vulnerability scanning", features: ["Full Port & Service Scanning", "Vulnerability Identification", "Security Hardening Checklist", "SSL & Encryption Checkup", "Executive Summary Report"] },
      { name: "Penetration Testing", price: "5,500", desc: "Active security testing & remediation", features: ["OWASP Top 10 Web Tests", "Active Exploit Penetration", "Source Code Security Review", "Vulnerability Remediation Help", "Certificate of Completion"], active: true },
      { name: "Enterprise Compliance", price: "10,000", desc: "Continuous security & policy audit", features: ["GDPR / ISO 27001 Compliance", "24/7 Log Intrusion Monitoring", "Employee Security Training", "Incident Response Playbook", "Quarterly Security Reviews"] },
    ]
  },
  "migration-maintenance": {
    subtitle: "Pricing Plans", title: "Migration & Maintenance Packages", type: "simple",
    items: [
      { name: "Basic Site Migration", price: "1,500", desc: "Safe migration with zero downtime", features: ["WordPress / Static Site Move", "Database Transfer & Sync", "DNS Routing & Propagation", "Post-migration Testing", "30 Days Site Warranty"] },
      { name: "Server & DB Migration", price: "4,000", desc: "Complex infrastructure migrations", features: ["Bare-metal to Cloud Move", "Database Schema Migration", "API Endpoint Integrations", "Minimum Interruption Windows", "90 Days Operational Support"], active: true },
      { name: "Annual Maintenance", price: "6,000", desc: "Complete peace of mind support package", features: ["Monthly Security Backups", "Uptime & Speed Auditing", "Content Updates & Bug Fixes", "24/7 Emergency Support Hotline", "1 Year Service Package SLA"], duration: "/yr" },
    ]
  },
};

// ─── Single pricing card ──────────────────────────────────────────────────────
function PriceCard({ item, currency, idx }) {
  const isActive = item.active;
  return (
    <div className="col-lg-4 col-md-6 mb-4" data-aos="fade-up" data-aos-delay={`${(idx + 1) * 80}`}>
      <div style={{
        background: "#fff",
        border: isActive ? "2px solid var(--color-primary, #2400A8)" : "1px solid #e5e5e5",
        borderRadius: "20px",
        padding: "38px 28px",
        textAlign: "center",
        position: "relative",
        boxShadow: isActive ? "0 12px 36px rgba(36,0,168,0.12)" : "0 4px 16px rgba(0,0,0,0.05)",
        transition: "transform 0.25s, box-shadow 0.25s",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-6px)"; e.currentTarget.style.boxShadow = "0 18px 48px rgba(36,0,168,0.16)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = isActive ? "0 12px 36px rgba(36,0,168,0.12)" : "0 4px 16px rgba(0,0,0,0.05)"; }}
      >
        {isActive && (
          <span style={{
            position: "absolute", top: "-13px", left: "50%", transform: "translateX(-50%)",
            background: "var(--color-primary, #2400A8)", color: "#fff",
            fontSize: "0.72rem", fontWeight: 700, padding: "4px 16px",
            borderRadius: "100px", letterSpacing: "0.8px", textTransform: "uppercase",
            fontFamily: "'Barlow', sans-serif", whiteSpace: "nowrap"
          }}>Most Popular</span>
        )}
        <h3 style={{
          fontSize: "1.2rem", fontWeight: 700, marginBottom: "6px",
          color: isActive ? "var(--color-primary, #2400A8)" : "#1a1a2e",
          fontFamily: "'Barlow', sans-serif"
        }}>{item.name}</h3>

        {item.timeline && (
          <span style={{ color: "var(--color-primary, #2400A8)", fontWeight: 600, fontSize: "0.78rem", letterSpacing: "0.4px", textTransform: "uppercase" }}>
            ⏱ Delivery: {item.timeline}
          </span>
        )}
        {item.desc && !item.timeline && (
          <p style={{ color: "#666", fontSize: "0.88rem", marginBottom: "6px", lineHeight: "1.5" }}>{item.desc}</p>
        )}

        {/* Price */}
        <div style={{ margin: "18px 0", display: "flex", alignItems: "baseline", justifyContent: "center", gap: "4px" }}>
          <span style={{ fontSize: "1rem", color: "#888", fontWeight: 600, alignSelf: "flex-start", paddingTop: "6px" }}>
            {SYMBOLS[currency]}
          </span>
          <span style={{ fontSize: "2.6rem", fontWeight: 800, color: "var(--color-primary, #2400A8)", lineHeight: 1 }}>
            {convertPrice(item.price, currency)}
          </span>
          {item.duration && (
            <span style={{ fontSize: "1rem", color: "#888", fontWeight: 600 }}>{item.duration}</span>
          )}
        </div>

        {/* Features */}
        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px 0", flexGrow: 1, textAlign: "left" }}>
          {item.features.map((f, fi) => (
            <li key={fi} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", fontSize: "0.88rem", color: "#444" }}>
              <i className="fa fa-check-circle" style={{ color: "var(--color-primary, #2400A8)", flexShrink: 0 }}></i>
              {f}
            </li>
          ))}
        </ul>

        <a
          href="#"
          onClick={e => { e.preventDefault(); if (typeof window !== "undefined" && window.$) { window.$("#quoteModal").addClass("active"); window.$("body").css("overflow", "hidden"); } }}
          style={{
            display: "block", width: "100%", padding: "12px 20px", borderRadius: "8px",
            background: isActive ? "var(--color-primary, #2400A8)" : "transparent",
            color: isActive ? "#fff" : "var(--color-primary, #2400A8)",
            border: "2px solid var(--color-primary, #2400A8)",
            fontWeight: 700, fontSize: "0.9rem", textDecoration: "none",
            fontFamily: "'Barlow', sans-serif", transition: "all 0.25s",
            letterSpacing: "0.4px",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "var(--color-primary, #2400A8)"; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={e => { e.currentTarget.style.background = isActive ? "var(--color-primary, #2400A8)" : "transparent"; e.currentTarget.style.color = isActive ? "#fff" : "var(--color-primary, #2400A8)"; }}
        >
          Choose Plan
        </a>
      </div>
    </div>
  );
}

// ─── Main PricingSection component ───────────────────────────────────────────
export default function PricingSection({ slug }) {
  const data = pricingCatalog[slug];
  const [currency, setCurrency] = useState("AED");
  const [activeTab, setActiveTab] = useState(0);

  if (!data) return null;

  const currencies = ["AED", "USD", "EUR"];

  return (
    <section style={{ background: "linear-gradient(180deg, #f4f6fb 0%, #eef0f7 100%)", padding: "80px 0" }} id="service-pricing">
      <div className="container">

        {/* ── Header ── */}
        <div className="text-center mb-5" data-aos="fade-down">
          <div className="subtitle subtitle_1bececf">
            <h4>
              <span className="subtitle-first-span"></span>
              <span className="subtitle-middle-span">{data.subtitle}</span>
              <span className="subtitle-last-span"></span>
            </h4>
          </div>
          <div className="title title_1bececf">
            <h2>
              <span></span>
              <span>{data.title}</span>
              <span></span>
            </h2>
          </div>
          {data.description && (
            <p style={{ color: "#666", maxWidth: "620px", margin: "12px auto 0", fontSize: "1rem", lineHeight: "1.7" }}>
              {data.description}
            </p>
          )}
        </div>

        {/* ── Currency Switcher ── */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "40px" }} data-aos="fade-up">
          <div style={{
            display: "inline-flex", background: "#fff", borderRadius: "100px",
            padding: "6px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", gap: "4px"
          }}>
            {currencies.map(cur => (
              <button
                key={cur}
                onClick={() => setCurrency(cur)}
                style={{
                  border: "none", cursor: "pointer",
                  padding: "10px 24px", borderRadius: "100px",
                  fontWeight: 700, fontSize: "0.88rem",
                  fontFamily: "'Barlow', sans-serif",
                  transition: "all 0.25s",
                  background: currency === cur ? "var(--color-primary, #2400A8)" : "transparent",
                  color: currency === cur ? "#fff" : "#555",
                  letterSpacing: "0.4px",
                  display: "flex", alignItems: "center", gap: "6px",
                }}
              >
                <span>{FLAGS[cur]}</span>
                <span>{cur}</span>
              </button>
            ))}
          </div>
          {/* Rate note */}
          {currency !== "AED" && (
            <div style={{ display: "flex", alignItems: "center", marginLeft: "16px", fontSize: "0.8rem", color: "#888", fontStyle: "italic" }}>
              ≈ 1 {currency} = {RATES[currency].toFixed(2)} AED (indicative)
            </div>
          )}
        </div>

        {/* ── Tabbed layout (web-app / ui-ux) ── */}
        {data.type === "tabbed" && (
          <>
            {/* Category Tab Buttons */}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "36px", flexWrap: "wrap", gap: "8px" }} data-aos="fade-up">
              <div style={{ background: "#efefef", borderRadius: "100px", padding: "6px", display: "inline-flex", flexWrap: "wrap", gap: "4px" }}>
                {data.categories.map((cat, idx) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveTab(idx)}
                    style={{
                      border: "none", cursor: "pointer",
                      padding: "9px 22px", borderRadius: "100px",
                      fontWeight: 700, fontSize: "0.85rem",
                      fontFamily: "'Barlow', sans-serif",
                      transition: "all 0.25s",
                      background: activeTab === idx ? "var(--color-primary, #2400A8)" : "transparent",
                      color: activeTab === idx ? "#fff" : "#444",
                    }}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Cards for active tab */}
            <div className="row justify-content-center">
              {data.categories[activeTab].items.map((item, idx) => (
                <PriceCard key={idx} item={item} currency={currency} idx={idx} />
              ))}
            </div>
          </>
        )}

        {/* ── Simple 3-plan layout ── */}
        {data.type === "simple" && (
          <div className="row justify-content-center">
            {data.items.map((item, idx) => (
              <PriceCard key={idx} item={item} currency={currency} idx={idx} />
            ))}
          </div>
        )}

        {/* ── Bottom note ── */}
        <div style={{ textAlign: "center", marginTop: "32px", color: "#888", fontSize: "0.82rem" }} data-aos="fade-up">
          <i className="fa fa-info-circle" style={{ marginRight: "6px" }}></i>
          All prices are in {SYMBOLS[currency]}. Currency rates are indicative. Final invoices are issued in AED.
          {" "}<a href="/contact" style={{ color: "var(--color-primary, #2400A8)", fontWeight: 600, textDecoration: "none" }}>Contact us</a> for a custom quote.
        </div>

      </div>
    </section>
  );
}
